import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { apiPost, apiPut } from "../../utils/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { AdminFormDialog, AdminField } from "./AdminFormDialog";
import { Input, Textarea } from "../ui/input";
import { ImageUpload } from "../subcomponents/ImageUpload";
import { RichEditor } from "../subcomponents/RichEditor";

type CenterFormState = {
  title: string;
  description: string;
  profile: string;
  coverImage: string;
  domaineInterventions: string;
  etudesRealisees: string;
  partenaires: string;
  contacts: string;
  directionName: string;
  directionRole: string;
  directionEmail: string;
  directionPhone: string;
};

const EMPTY_CENTER: CenterFormState = {
  title: "",
  description: "",
  profile: "",
  coverImage: "",
  domaineInterventions: "",
  etudesRealisees: "",
  partenaires: "",
  contacts: "",
  directionName: "",
  directionRole: "",
  directionEmail: "",
  directionPhone: "",
};

const splitLines = (s: string) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const joinLines = (arr?: any[]) => (arr || []).join("\n");

type Props = {
  open: boolean;
  editing: any | null;
  isAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const FORM_ID = "center-form";

export const AdminCenterModal: React.FC<Props> = ({ open, editing, isAdmin, onClose, onSaved }) => {
  const { t } = useLanguage();

  const schema = Yup.object({
    title: Yup.string().trim().required(t.common.required),
    description: Yup.string().trim().required(t.common.required),
    profile: Yup.string(),
    coverImage: Yup.string(),
    domaineInterventions: Yup.string(),
    etudesRealisees: Yup.string(),
    partenaires: Yup.string(),
    contacts: Yup.string(),
    directionName: Yup.string(),
    directionRole: Yup.string(),
    directionEmail: Yup.string(),
    directionPhone: Yup.string(),
  });

  const initial: CenterFormState = editing
    ? {
        title: editing.title,
        description: editing.description || "",
        profile: editing.profile || "",
        coverImage: editing.coverImage || "",
        domaineInterventions: joinLines(editing.domaineInterventions),
        etudesRealisees: joinLines(editing.etudesRealisees),
        partenaires: joinLines(editing.partenaires),
        contacts: joinLines(editing.contacts),
        directionName: editing.direction?.name || "",
        directionRole: editing.direction?.role || "",
        directionEmail: (editing.direction?.email || []).join(", "),
        directionPhone: (editing.direction?.phone || []).join(", "),
      }
    : EMPTY_CENTER;

  const title = editing ? t.admin.editCenter : t.admin.newCenter;
  const submitLabel = editing ? t.admin.save : isAdmin ? t.admin.publish : t.admin.submitForReview;

  return (
    <Formik
      initialValues={initial}
      validationSchema={schema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        const direction = values.directionName.trim()
          ? {
              name: values.directionName.trim(),
              role: values.directionRole.trim() || "Directeur",
              slug: values.directionName.toLowerCase().replace(/\s+/g, "-"),
              email: values.directionEmail.split(",").map((s) => s.trim()).filter(Boolean),
              phone: values.directionPhone.split(",").map((s) => s.trim()).filter(Boolean),
            }
          : null;
        const payload = {
          title: values.title.trim(),
          description: values.description.trim(),
          profile: values.profile || "",
          coverImage: values.coverImage || "",
          direction,
          domaineInterventions: splitLines(values.domaineInterventions),
          etudesRealisees: splitLines(values.etudesRealisees),
          partenaires: splitLines(values.partenaires),
          contacts: splitLines(values.contacts),
        };
        try {
          if (editing) {
            await apiPut(`/centers/${editing.id}`, payload);
            toast.success(t.common.saveSuccess);
          } else {
            await apiPost("/centers", payload);
            toast.success(isAdmin ? t.admin.published : t.admin.submitted);
          }
          onSaved();
          onClose();
        } catch (e: any) {
          toast.error(e?.response?.data?.reason || e?.messageText || t.common.error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <AdminFormDialog
          open={open}
          onClose={onClose}
          title={title}
          subtitle={t.admin.pageContent}
          testId="center-modal"
          className="max-w-3xl"
          formId={FORM_ID}
          submitLabel={submitLabel}
          isSubmitting={isSubmitting}
        >
          <Form id={FORM_ID} className="grid gap-4 pb-4">
            <AdminField label={t.admin.centerName} required error={touched.title ? errors.title : undefined}>
              <Field as={Input} name="title" data-testid="center-title" />
            </AdminField>
            <AdminField label={t.admin.shortDescription} required error={touched.description ? errors.description : undefined}>
              <Field as={Textarea} name="description" data-testid="center-description" rows={3} />
            </AdminField>
            <ImageUpload
              value={values.coverImage}
              onChange={(url) => setFieldValue("coverImage", url)}
              folder="ulpgl/uploads"
              label={t.admin.coverImageOptional}
              testId="center-cover"
            />

            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-foreground mb-3">{t.admin.direction}</p>
              <div className="grid grid-cols-2 gap-3">
                <Field as={Input} name="directionName" placeholder={t.admin.directorName} />
                <Field as={Input} name="directionRole" placeholder={t.admin.directorRole} />
                <Field as={Input} name="directionEmail" placeholder={t.admin.directorEmails} />
                <Field as={Input} name="directionPhone" placeholder={t.admin.directorPhones} />
              </div>
            </div>

            <p className="text-xs text-muted-foreground">{t.admin.oneLinePerEntry}</p>
            <AdminField label={t.admin.interventionAreas}>
              <Field as={Textarea} name="domaineInterventions" rows={3} />
            </AdminField>
            <AdminField label={t.admin.completedStudies}>
              <Field as={Textarea} name="etudesRealisees" rows={3} />
            </AdminField>
            <AdminField label={t.admin.partners}>
              <Field as={Textarea} name="partenaires" rows={3} />
            </AdminField>
            <AdminField label={t.admin.contacts}>
              <Field as={Textarea} name="contacts" rows={2} />
            </AdminField>
            <AdminField label={t.admin.detailedProfile}>
              <RichEditor
                value={values.profile}
                onChange={(html) => setFieldValue("profile", html)}
                folder="ulpgl/uploads"
                placeholder={t.admin.centerProfilePlaceholder}
              />
            </AdminField>
          </Form>
        </AdminFormDialog>
      )}
    </Formik>
  );
};
